import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
  STATUS_BADGE_TYPE,
} from "../../../../../../../../components";
import { MuiDateFieldFormController } from "../../../../../../../../components/dateField/MuiDateFieldFormController";
import {
  MAX_LENGTH_100,
  MAX_LENGTH_75,
  MAX_LENGTH_9,
  TM_INDIVIDUAL_ASSESSMENT,
  TM_LEADER_COACHING,
  WAVE_COHORT_SITE_CORE_KEY,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import { getFieldRuleByCountry } from "../../../../../../../../helpers/constants/countryFieldRulesUtils";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";
import {
  calculateAge,
  getMaxAllowedPastDate,
  getTomorrowDate,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import { programStatusSelector } from "../../../../../../../../redux/slices/program/programSelector";
import {
  ParticipantTabFormKey,
  ParticipantTabFormType,
  ProgramDetail,
} from "../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

interface ParticipantInformationProps {
  pageId: string;
  programData?: ProgramDetail;
  isEditProgram?: boolean;
  editProgramParticipantData?: ParticipantTabFormType;
  languageOptions?: OptionsDataType[];
  isNextRegister?: boolean;
}

export const ParticipantInformation: FC<ParticipantInformationProps> = ({
  pageId,
  programData,
  isEditProgram,
  editProgramParticipantData,
  languageOptions,
  isNextRegister = false,
}) => {
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { control, setValue, getValues, watch } = useFormContext();
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchDropdownValuesByPrefix,
    fetchValidationMessage,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const programStatus = useSelector(programStatusSelector);
  const [showWaveCohortField, setShowWaveCohortField] =
    useState<boolean>(false);
  const location = useLocation();
  const isFromNIS = location.state?.isFromNISParticipant ?? false;

  const isProgramStatusOpen = useMemo(() => {
    return programStatus === STATUS_BADGE_TYPE.open;
  }, [programStatus]);

  const deliveryCountry = useMemo(
    () => getValueOrBlank(programData?.deliveryCountry),
    [programData?.deliveryCountry],
  );

  const genderRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.gender,
  );

  const dateOfBirthRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.dateOfBirth,
  );

  const personalIdRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.personalId,
  );
  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.participantInformation, key);
  };

  const tomorrowDate = getTomorrowDate();

  const yesterdayDate = getMaxAllowedPastDate();

  const maxDate = dayjs().subtract(18, "years").startOf("day");
  // 18 years ago, at the start of the day

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(programSiteCoreKeys.participantInformation, key);
  };

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const isTMAssessmentProgram = useMemo(() => {
    return programData?.tmProgramType === TM_INDIVIDUAL_ASSESSMENT;
  }, [programData?.tmProgramType]);

  const isTMLeaderCoachingProgram = useMemo(() => {
    return programData?.tmProgramType === TM_LEADER_COACHING;
  }, [programData?.tmProgramType]);

  const handleDobChange = (newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(ParticipantTabFormKey.dateOfBirth, dateValue);

    setValue(
      ParticipantTabFormKey.age,
      calculateAge(dateValue, getValueOrBlank(userCountryDateFormat)),
    );
  };

  const countrySpecificOptions = useMemo(() => {
    const educationLevelOptions = fetchDropdownValuesByPrefix(
      programSiteCoreKeys.participantInformation,
      programSiteCoreKeys.educationLevelListPrefix,
      deliveryCountry,
    );

    return { educationLevelOptions };
  }, [deliveryCountry, editProgramParticipantData, siteCoreLoading]);

  const ageRangeRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.ageRange,
  );

  const educationLevelRangeRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.educationLevel,
  );

  const fieldSpecialCharactersTooltipMessage = fetchValidationMessage(
    commonSiteCoreKeys.fieldSpecialCharactersTooltipMessage,
  );
  const tooltipHeader = fetchValidationMessage(
    commonSiteCoreKeys.allowedSpecialCharacters,
  );

  useEffect(() => {
    const selectedBatchType = getValues(ParticipantTabFormKey.batchTypeKey);
    if (selectedBatchType === WAVE_COHORT_SITE_CORE_KEY) {
      setShowWaveCohortField(true);
      setValue(
        ParticipantTabFormKey.waveCohort,
        getValues(ParticipantTabFormKey.waveCohort),
      );
    } else {
      setValue(ParticipantTabFormKey.waveCohort, "");
      setShowWaveCohortField(false);
    }
  }, [watch(ParticipantTabFormKey.batchTypeKey)]);

  return (
    <MuiBox className="participant-info" data-testid="participant-info">
      <MuiGrid container className="form-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController control={control} name={ParticipantTabFormKey.prefix}>
            <MuiSelect
              label={getLabel(programSiteCoreKeys.prefix)}
              options={getDropdownValues(programSiteCoreKeys.prefixList)}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.prefix}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              disabled
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.firstName}
          >
            <MuiTextField
              label={getLabel(programSiteCoreKeys.firstName)}
              value=""
              id={`${pageId}-${ParticipantTabFormKey.firstName}`}
              disabled
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_100}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.lastName}
          >
            <MuiTextField
              label={getLabel(programSiteCoreKeys.lastName)}
              value=""
              disabled
              id={`${pageId}-${ParticipantTabFormKey.lastName}`}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_100}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.preferredName}
          >
            <MuiTextField
              label={getLabel(programSiteCoreKeys.preferredName)}
              value=""
              id={`${pageId}-${ParticipantTabFormKey.preferredName}`}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_100}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.ageRange}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.ageRange)}
              options={fetchDropdownValues(
                programSiteCoreKeys.participantInformation,
                programSiteCoreKeys.ageRangeList,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.ageRange}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              isMandatory={ageRangeRule.mandatory}
            />
          </FormController>
        </MuiGrid>

        {dateOfBirthRule.visible && (
          <>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantTabFormKey.dateOfBirth}
                label={getLabel(programSiteCoreKeys.dateOfBirth)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                className="date-picker"
                maxDate={maxDate}
                onChange={handleDobChange}
                id={`${pageId}-${ParticipantTabFormKey.dateOfBirth}`}
                disableManualInput
                value={
                  getValues(ParticipantTabFormKey.dateOfBirth) !== ""
                    ? dayjs(
                        getValues(ParticipantTabFormKey.dateOfBirth),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
                isMandatory={dateOfBirthRule.mandatory}
              />
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={ParticipantTabFormKey.age}
              >
                <MuiTextField
                  label={getLabel(programSiteCoreKeys.age)}
                  id={`${pageId}-${ParticipantTabFormKey.age}`}
                  disabled
                  value={getValues(ParticipantTabFormKey.age)}
                  showCloseIcon
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </>
        )}

        {genderRule.visible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.gender}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.gender)}
                options={getDropdownValues(programSiteCoreKeys.genderList)}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.gender}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                isMandatory={genderRule.mandatory}
              />
            </FormController>
          </MuiGrid>
        )}

        {personalIdRule.visible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.personalId}
            >
              <MuiTextField
                label={getLabel(programSiteCoreKeys.personalId)}
                id={`${pageId}-${ParticipantTabFormKey.personalId}`}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
                value=""
                placeholder={getLabel(
                  programSiteCoreKeys.personalIdPlaceholderText,
                )}
                maxLength={MAX_LENGTH_9}
                isMandatory={personalIdRule.mandatory}
              />
            </FormController>
          </MuiGrid>
        )}

        {!isTMAssessmentProgram && !isTMLeaderCoachingProgram && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.spouse}
            >
              <MuiTextField
                label={getLabel(programSiteCoreKeys.spousePartner)}
                value=""
                id={`${pageId}-${ParticipantTabFormKey.spouse}`}
                placeholder={commonLabels.inputPlaceHolder}
                showCloseIcon
                maxLength={MAX_LENGTH_100}
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.educationLevel}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.educationLevel)}
              options={countrySpecificOptions.educationLevelOptions}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.educationLevel}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              isMandatory={educationLevelRangeRule.mandatory}
            />
          </FormController>
        </MuiGrid>

        {!isTMAssessmentProgram && !isTMLeaderCoachingProgram && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDateFieldFormController
              control={control}
              name={ParticipantTabFormKey.mustStartByDate}
              label={getLabel(programSiteCoreKeys.mustStartByDate)}
              valueFormat={userCountryDateFormat}
              format={userCountryDateFormat}
              className="date-picker"
              minDate={tomorrowDate}
              disabled={!isProgramStatusOpen}
              id={`${pageId}-${ParticipantTabFormKey.mustStartByDate}`}
              value={
                getValues(ParticipantTabFormKey.mustStartByDate) !== ""
                  ? dayjs(
                      getValues(ParticipantTabFormKey.mustStartByDate),
                      userCountryDateFormat,
                    )
                  : null
              }
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
              clearable
            />
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDateFieldFormController
            control={control}
            name={ParticipantTabFormKey.actualStartDate}
            label={getLabel(programSiteCoreKeys.actualStartDate)}
            valueFormat={userCountryDateFormat}
            format={userCountryDateFormat}
            className="date-picker"
            id={`${pageId}-${ParticipantTabFormKey.actualStartDate}`}
            disableManualInput
            value={
              isNotEmpty(getValues(ParticipantTabFormKey.actualStartDate))
                ? dayjs(
                    getValues(ParticipantTabFormKey.actualStartDate),
                    userCountryDateFormat,
                  )
                : null
            }
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
            disabled //it will be disabled only
            disableLabel
          />
        </MuiGrid>

        {isEditProgram && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDateFieldFormController
              control={control}
              name={ParticipantTabFormKey.projectedProgramEndDate}
              label={getLabel(programSiteCoreKeys.projectedProgramEndDate)}
              valueFormat={userCountryDateFormat}
              format={userCountryDateFormat}
              disabled={isProgramStatusOpen}
              className="date-picker"
              minDate={tomorrowDate}
              id={`${pageId}-${ParticipantTabFormKey.projectedProgramEndDate}`}
              disableManualInput
              value={
                getValues(ParticipantTabFormKey.projectedProgramEndDate) !== ""
                  ? dayjs(
                      getValues(ParticipantTabFormKey.projectedProgramEndDate),
                      userCountryDateFormat,
                    )
                  : null
              }
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
            />
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.primaryLanguage}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.primaryLanguage)}
              options={getDropdownValues(programSiteCoreKeys.languageList)}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.primaryLanguage}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        {isNextRegister && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.preferredLanguage}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.preferredLanguage)}
                options={languageOptions ?? []}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.preferredLanguage}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        )}

        {!isTMAssessmentProgram && !isTMLeaderCoachingProgram && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.nisId}
            >
              <MuiTextField
                label={getLabel(programSiteCoreKeys.nisId)}
                value=""
                id={`${pageId}-${ParticipantTabFormKey.nisId}`}
                placeholder={commonLabels.inputPlaceHolder}
                showCloseIcon={!isFromNIS}
                disabled={isFromNIS}
                maxLength={MAX_LENGTH_100}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        )}

        {isEditProgram && (
          <>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantTabFormKey.textSubscribedDate}
                label={getLabel(programSiteCoreKeys.textSubscribedDate)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                className="date-picker"
                minDate={tomorrowDate}
                id={`${pageId}-${ParticipantTabFormKey.textSubscribedDate}`}
                clearable
                value={
                  getValues(ParticipantTabFormKey.textSubscribedDate) !== ""
                    ? dayjs(
                        getValues(ParticipantTabFormKey.textSubscribedDate),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
              />
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantTabFormKey.textUnsubscribedDate}
                label={getLabel(programSiteCoreKeys.textUnsubscribedDate)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                className="date-picker"
                minDate={tomorrowDate}
                id={`${pageId}-${ParticipantTabFormKey.textUnsubscribedDate}`}
                clearable
                value={
                  getValues(ParticipantTabFormKey.textUnsubscribedDate) !== ""
                    ? dayjs(
                        getValues(ParticipantTabFormKey.textUnsubscribedDate),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
              />
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantTabFormKey.emailUnsubscribedDate}
                label={getLabel(programSiteCoreKeys.emailUnsubscribedDate)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                className="date-picker"
                maxDate={yesterdayDate}
                id={`${pageId}-${ParticipantTabFormKey.emailUnsubscribedDate}`}
                value={
                  getValues(ParticipantTabFormKey.emailUnsubscribedDate) !== ""
                    ? dayjs(
                        getValues(ParticipantTabFormKey.emailUnsubscribedDate),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
                clearable
              />
            </MuiGrid>
          </>
        )}
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiTextField
            label={getLabel(programSiteCoreKeys.tPSBlacklistStatus)}
            value={
              editProgramParticipantData?.isTPSBlacklist
                ? commonLabels.yes
                : commonLabels.no
            }
            id={`${pageId}-${ParticipantTabFormKey.tpsStatus}`}
            placeholder={commonLabels.inputPlaceHolder}
            showCloseIcon
            disabled
            requiredARIALabel={commonARIALabel.required}
          />
        </MuiGrid>

        {isTMAssessmentProgram && (
          <>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={ParticipantTabFormKey.batchTypeKey}
              >
                <MuiSelect
                  label={getLabel(programSiteCoreKeys.batchType)}
                  options={getDropdownValues(programSiteCoreKeys.batchTypeList)}
                  id={`${pageId}-${ParticipantTabFormKey.batchTypeKey}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  visibleDefaultSelectOption
                />
              </FormController>
            </MuiGrid>

            {showWaveCohortField && (
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <FormController
                  control={control}
                  name={ParticipantTabFormKey.waveCohort}
                >
                  <MuiTextField
                    label={getLabel(programSiteCoreKeys.waveCohortDetails)}
                    id={`${pageId}-${ParticipantTabFormKey.waveCohort}`}
                    placeholder={commonLabels.inputPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                    maxLength={MAX_LENGTH_75}
                  />
                </FormController>
              </MuiGrid>
            )}
          </>
        )}
      </MuiGrid>
    </MuiBox>
  );
};
